import { defineMessages } from "react-intl";

export const messages = defineMessages({
  appErrorTitle: {
    id: "shared.app_error.title",
    defaultMessage: "Uh oh. Something went wrong.",
    description: "Title for the application-level error page.",
  },
  appErrorDescription: {
    id: "shared.app_error.description",
    defaultMessage: "Please try again later.",
    description: "Description for the application-level error page.",
  },
  goBackHome: {
    id: "shared.app_error.action.go_home",
    defaultMessage: "Go back home",
    description: "Action for returning home from the application-level error page.",
  },
  cookiesTitle: {
    id: "shared.cookies.banner.title",
    defaultMessage: "We use cookies",
    description: "Title for the cookie-consent banner.",
  },
  cookiesDescription: {
    id: "shared.cookies.banner.description",
    defaultMessage:
      "Cookies help this site function, measure usage, and support marketing. <manageCookies>Manage</manageCookies> your cookie preferences anytime. Learn more about our <cookiePolicy>cookie policy</cookiePolicy>.",
    description:
      "Description for the cookie-consent banner, including preference and policy links.",
  },
  manageCookies: {
    id: "shared.cookies.action.manage",
    defaultMessage: "Manage Cookies",
    description: "Action for opening cookie preferences.",
  },
  yourPrivacyChoices: {
    id: "shared.cookies.action.your_privacy_choices",
    defaultMessage: "Your Privacy Choices",
    description:
      "Short footer button label for opening cookie preferences for US users. California users also see a privacy-options icon beside it.",
  },
  rejectNonEssentialCookies: {
    id: "shared.cookies.action.reject_non_essential",
    defaultMessage: "Reject non-essential",
    description: "Action for rejecting non-essential cookies.",
  },
  acceptAllCookies: {
    id: "shared.cookies.action.accept_all",
    defaultMessage: "Accept all",
    description: "Action for accepting all cookies.",
  },
  dismissCookieBanner: {
    id: "shared.cookies.action.dismiss_banner",
    defaultMessage: "Dismiss cookie banner",
    description: "Accessible label for dismissing the cookie banner without changing consent.",
  },
  cookiePreferencesTitle: {
    id: "shared.cookies.preferences.title",
    defaultMessage: "Cookie Preferences Center",
    description: "Title for the cookie-preferences modal.",
  },
  cookiePreferencesDescription: {
    id: "shared.cookies.preferences.description",
    defaultMessage:
      "Using websites and apps involves storing and retrieving information from your device, including cookies and other identifiers. You can change your consent at any time. <learnMore>Learn more</learnMore>.",
    description: "Description for the cookie-preferences modal, including a policy link.",
  },
  strictlyNecessaryCookies: {
    id: "shared.cookies.preferences.strictly_necessary.label",
    defaultMessage: "Strictly necessary cookies (always active)",
    description: "Label for required cookies.",
  },
  strictlyNecessaryCookiesDescription: {
    id: "shared.cookies.preferences.strictly_necessary.description",
    defaultMessage:
      "These cookies are essential for the site to function and cannot be toggled off. They assist with security and authentication.",
    description: "Description for required cookies.",
  },
  analyticsCookies: {
    id: "shared.cookies.preferences.analytics.label",
    defaultMessage: "Analytics cookies",
    description: "Label for analytics cookies.",
  },
  analyticsCookiesDescription: {
    id: "shared.cookies.preferences.analytics.description",
    defaultMessage:
      "These cookies help us understand how visitors interact with our site and improve performance.",
    description: "Description for analytics cookies.",
  },
  marketingCookies: {
    id: "shared.cookies.preferences.marketing.label",
    defaultMessage: "Marketing performance cookies",
    description: "Label for marketing-performance cookies.",
  },
  marketingCookiesDescription: {
    id: "shared.cookies.preferences.marketing.description",
    defaultMessage: "These cookies help us measure the effectiveness of our marketing efforts.",
    description: "Description for marketing-performance cookies.",
  },
  personalizationCookies: {
    id: "shared.cookies.preferences.personalization.label",
    defaultMessage: "Personalization cookies",
    description: "Label for personalization cookies.",
  },
  personalizationCookiesDescription: {
    id: "shared.cookies.preferences.personalization.description",
    defaultMessage: "These cookies help us personalize your experience.",
    description: "Description for personalization cookies.",
  },
  done: {
    id: "shared.action.done",
    defaultMessage: "Done",
    description: "Action for finishing a shared dialog.",
  },
  selectAllRows: {
    id: "shared.table.select_all_rows",
    defaultMessage: "Select all rows",
    description: "Accessible label for selecting all table rows.",
  },
  sortByColumn: {
    id: "shared.table.sort_by_column",
    defaultMessage: "Sort by {column}",
    description: "Accessible label for sorting a table by a column.",
  },
  resizeColumn: {
    id: "shared.table.resize_column",
    defaultMessage: "Resize {column} column",
    description: "Accessible label for resizing a table column.",
  },
  expandRow: {
    id: "shared.table.expand_row",
    defaultMessage: "Expand {row}",
    description: "Accessible label for expanding a table row.",
  },
  collapseRow: {
    id: "shared.table.collapse_row",
    defaultMessage: "Collapse {row}",
    description: "Accessible label for collapsing a table row.",
  },
  adsManagerBeta: {
    id: "shared.ads_manager_beta",
    defaultMessage: "Ads Manager Beta",
    description: "OpenAI Ads Manager beta product label.",
  },
  pageNavigationProgress: {
    id: "shared.navigation.progress",
    defaultMessage: "Page navigation progress",
    description: "Accessible label for page navigation progress.",
  },
  applyingChanges: {
    id: "shared.progress.applying_changes",
    defaultMessage: "Applying changes",
    description: "Title for progress while changes are being applied.",
  },
  loadingTotals: {
    id: "shared.table.loading_totals",
    defaultMessage: "Loading totals",
    description: "Placeholder shown while table totals load.",
  },
  embeddedVideo: {
    id: "shared.media.embedded_video",
    defaultMessage: "Embedded video",
    description: "Fallback accessible title for an embedded video.",
  },
  notFoundTitle: {
    id: "shared.not_found.title",
    defaultMessage: "Page not found",
    description: "Title for the not-found page.",
  },
  notFoundDescription: {
    id: "shared.not_found.description",
    defaultMessage: "We couldn't find the page you were looking for.",
    description: "Description for the not-found page.",
  },
  adsBrand: {
    id: "shared.ads_brand",
    defaultMessage: "Ads",
    description: "OpenAI Ads product label.",
  },
  onboardingUnavailableTitle: {
    id: "shared.unavailable.onboarding_paused.title",
    defaultMessage: "Ads onboarding is temporarily unavailable",
    description: "Title shown while Ads onboarding is paused.",
  },
  unavailableTitle: {
    id: "shared.unavailable.title",
    defaultMessage: "Coming soon",
    description: "Generic unavailable-page title.",
  },
  onboardingUnavailableDescription: {
    id: "shared.unavailable.onboarding_paused.description",
    defaultMessage:
      "Account creation and invitation flows are paused. Existing advertisers can continue using Ads Manager.",
    description: "Description shown while Ads onboarding is paused.",
  },
  unavailableDescription: {
    id: "shared.unavailable.description",
    defaultMessage: "Stay tuned for more information about advertising with OpenAI.",
    description: "Generic unavailable-page description.",
  },
  accountAccessChangedTitle: {
    id: "shared.account_recovery.title",
    defaultMessage: "Choose another ad account",
    description: "Title shown when the previously selected ad account is no longer accessible.",
  },
  accountRecoveryLoadingDescription: {
    id: "shared.account_recovery.loading.description",
    defaultMessage:
      "Your saved ad account is no longer available. We're finding the other ad accounts you can access.",
    description: "Description shown while the recovery picker loads accessible ad accounts.",
  },
  accountRecoveryErrorDescription: {
    id: "shared.account_recovery.error.description",
    defaultMessage:
      "Your saved ad account is no longer available, and we couldn't load your other accounts. Try again.",
    description: "Description shown when the recovery picker cannot load accessible ad accounts.",
  },
  accountRecoveryChooseDescription: {
    id: "shared.account_recovery.choose.description",
    defaultMessage:
      "Your saved ad account is no longer available. Select another account to continue.",
    description: "Description shown when the recovery picker has accessible accounts.",
  },
  accountRecoveryEmptyDescription: {
    id: "shared.account_recovery.empty.description",
    defaultMessage:
      "Your saved ad account is no longer available, and we couldn't find another account you can access.",
    description: "Description shown when account recovery finds no accessible ad accounts.",
  },
  findAdAccount: {
    id: "shared.account_recovery.action.find_account",
    defaultMessage: "Find or create an ad account",
    description: "Action for continuing to onboarding when account recovery finds no accounts.",
  },
  goToAdsManager: {
    id: "shared.unavailable.action.go_to_ads_manager",
    defaultMessage: "Go to Ads Manager",
    description: "Action for opening Ads Manager from the unavailable page.",
  },
  learnMore: {
    id: "shared.action.learn_more",
    defaultMessage: "Learn more",
    description: "Shared action for learning more.",
  },
  logOut: {
    id: "shared.action.log_out",
    defaultMessage: "Log out",
    description: "Shared action for logging out.",
  },
  loginFailedTitle: {
    id: "shared.login_error.title",
    defaultMessage: "Login Failed",
    description: "Title for the login-error page.",
  },
  loginFailedDescription: {
    id: "shared.login_error.description",
    defaultMessage:
      "An error occurred during login. Please try again.<lineBreak></lineBreak>If the issue persists, contact support.",
    description: "Description for the login-error page.",
  },
  loginFailedEnvironmentHint: {
    id: "shared.login_error.environment_hint",
    defaultMessage:
      "(In {environment}, this page may be shown if an exit node is not used during authentication)",
    description: "Development and staging hint for the login-error page.",
  },
  tryAgain: {
    id: "shared.action.try_again",
    defaultMessage: "Try Again",
    description: "Shared action for retrying a failed operation.",
  },
});
